'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, ArrowRight, RotateCw, X } from 'lucide-react';

interface Props {
  redirectUri: string | null;
  authorizationId: string | null;
}

export function OAuthMenubar({ redirectUri, authorizationId }: Props) {
  const nav = typeof window !== 'undefined' ? (window as any).navigation : undefined;
  const [canGoBack, setCanGoBack] = useState(true);
  const [canGoForward, setCanGoForward] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const navigatingRef = useRef(false);

  useEffect(() => {
    if (!nav) return;
    const updateNavButtons = () => {
      setCanGoBack(nav.canGoBack);
      setCanGoForward(nav.canGoForward);
    };
    updateNavButtons();
    nav.addEventListener('navigatesuccess', updateNavButtons);
    return () => nav.removeEventListener('navigatesuccess', updateNavButtons);
  }, [nav]);

  const handleClose = useCallback(async () => {
    if (canceling || navigatingRef.current) return;
    setCanceling(true);

    try {
      const res = await fetch('/api/v1/select/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authorization_id: authorizationId }),
      });
      const data = await res.json();

      if (data.redirect_url) {
        navigatingRef.current = true;
        window.location.href = data.redirect_url;
        return;
      }
    } catch (err) {
      console.error('[OAuthMenubar] Cancel endpoint failed, using fallback:', err);
    }

    // Fallback: if cancel endpoint failed (expired cookie, network error),
    // use the redirectUri prop that was passed at render time
    if (redirectUri) {
      try {
        const fallbackUrl = new URL(redirectUri);
        fallbackUrl.searchParams.set('canceled', 'true');
        navigatingRef.current = true;
        window.location.href = fallbackUrl.toString();
        return;
      } catch (err) {
        console.error('[OAuthMenubar] Invalid redirectUri:', redirectUri, err);
      }
    }

    setCanceling(false);
  }, [canceling, redirectUri, authorizationId]);

  const buttonClass = 'cursor-pointer disabled:cursor-default py-2';

  return (
    <div className="w-full flex bg-black items-center justify-center px-3 md:px-5 border-b border-zinc-800 sticky top-0 z-50">
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center gap-5">
          <button
            onClick={() => window.history.back()}
            disabled={!canGoBack}
            className={`${buttonClass} ${canGoBack ? 'text-white' : 'text-zinc-500'}`}
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <button
            onClick={() => window.history.forward()}
            disabled={!canGoForward}
            className={`${buttonClass} ${canGoForward ? 'text-white' : 'text-zinc-500'}`}
            aria-label="Go forward"
          >
            <ArrowRight size={20} />
          </button>
          <button
            onClick={() => window.location.reload()}
            className={`${buttonClass} text-white`}
            aria-label="Refresh"
          >
            <RotateCw size={18} />
          </button>
        </div>

        {redirectUri && (
          <button
            onClick={handleClose}
            disabled={canceling}
            className={`${buttonClass} text-white`}
            aria-label="Close and return to app"
          >
            <X size={22} />
          </button>
        )}
      </div>
    </div>
  );
}
